import { chargeInvoice } from 'actions/paymentActions';

import {
  ALL_PAYMENT_METHOD_KEYS,
  PAYMENT_METHOD_KEY,
  PAYMENT_METHOD_TYPE_MAP,
} from 'tools/constants/allowedPaymentMethods';
import {
  AccountSummaryStatus,
  PaymentScheduleStatus,
  PaymentStatus,
} from 'tools/constants/payment';
import {
  ExistingPaymentMethodForInvoice,
  ExistingPaymentMethods,
} from 'tools/constants/payPortal';

import { PAYMENT_METHOD_API_URL } from 'tools/urls/payment';

import {
  CUSTOM_AMOUNT_HELPER_TEXT,
  DETERMINE_PAYMENT_METHOD_RETURN_TYPE,
  PAYMENT_GATEWAY_NAME,
  PAYMENT_METHOD,
  PAYMENT_METHOD_TYPE,
} from 'constants/payment';

import type {
  BuildExistingPaymentMethodOptionsResult,
  ChargeResponseData,
  PaymentMethod,
  PaymentProcessingFee,
  PaymentSummary,
  ValueFormat,
} from 'types/payment';

/**
 * Check if custom payment amounts are allowed
 *
 * Returns true if either partial payments or overpayments are enabled
 * in the school's payment configuration.
 *
 * @param paymentData - Payment summary data containing school config
 * @returns Whether custom amounts are allowed
 */
export function isCustomAmountAllowed(paymentData?: PaymentSummary): boolean {
  return (
    Boolean(paymentData?.SchoolPaymentConfig?.enablePartialPayment) ||
    Boolean(paymentData?.SchoolPaymentConfig?.enableOverPayment)
  );
}

/**
 * Build the helper text shown under the custom amount field.
 *
 * @param paymentData - Payment summary data containing school config
 * @returns The helper text to display, or '' if custom amounts are disabled
 */
export function getCustomAmountHelperText(
  paymentData?: PaymentSummary,
): string {
  const config = paymentData?.SchoolPaymentConfig;
  const canPayLess = Boolean(config?.enablePartialPayment);
  const canPayMore = Boolean(config?.enableOverPayment);

  if (canPayLess && canPayMore) {
    return CUSTOM_AMOUNT_HELPER_TEXT.BOTH;
  }
  if (canPayLess) {
    return CUSTOM_AMOUNT_HELPER_TEXT.LESS;
  }
  return '';
}

/**
 * Calculate the processing fee amount for a payment.
 *
 * Single source of truth for fee math: fee = (amount * rate / 100) + flatFee,
 * computed in cents to avoid floating-point drift.
 *
 * @param amount - The base payment amount in dollars
 * @param fee - Processing fee with rate (%) and flatFee ($)
 * @returns Fee amount in dollars, rounded to cents
 */
export const calculateFeeAmount = (
  amount: number,
  fee: PaymentProcessingFee,
) => {
  // Step 1: convert to cents
  const amountCents = Math.round((amount + Number.EPSILON) * 100);

  // Step 2: calculate fee in cents (rate percentage plus flat fee)
  const feeCents = Math.round(
    (amountCents * fee.rate) / 100 + fee.flatFee * 100 + Number.EPSILON,
  );

  // Step 3: convert back to dollars
  return feeCents / 100;
};

/**
 * Format a processing fee for display.
 *
 * Shows both components when a flat fee applies, e.g. "2.9% + $0.30";
 * otherwise just the rate, e.g. "2.9%".
 *
 * @param fee - Processing fee with rate (%) and flatFee ($)
 * @returns Display label string
 */
export const formatProcessingFee = ({
  flatFee,
  rate,
}: PaymentProcessingFee): string =>
  flatFee > 0 ? `${rate}% + $${flatFee.toFixed(2)}` : `${rate}%`;

/**
 * Check whether a processing fee has any chargeable component.
 *
 * @param fee - Processing fee, possibly absent
 * @returns Whether either the rate or the flat fee is non-zero
 */
export const hasProcessingFee = (
  fee: PaymentProcessingFee | null | undefined,
): boolean => Boolean(fee && (fee.rate > 0 || fee.flatFee > 0));

/**
 * Normalize a fee value from the API into the { rate, flatFee } shape.
 *
 * Accepts the legacy scalar percentage (pre fee-structure rollout) as well as
 * the new object shape, and treats null/undefined as a zero fee.
 *
 * @param fee - Scalar percentage, fee object, or absent
 * @returns Normalized processing fee object
 */
export const normalizeProcessingFee = (
  fee: number | PaymentProcessingFee | null | undefined,
): PaymentProcessingFee =>
  typeof fee === 'number'
    ? { flatFee: 0, rate: fee }
    : { flatFee: fee?.flatFee ?? 0, rate: fee?.rate ?? 0 };

/**
 * Format a card's last four digits as a masked number, e.g. "xxxxxxxxxxxx4242".
 *
 * @param lastFour - The card's last four digits
 * @returns Masked card number, or empty string when lastFour is missing
 */
export const getMaskedCardNumber = (lastFour?: string): string =>
  lastFour ? `xxxxxxxxxxxx${lastFour}` : '';

/**
 * Format a recipient's city/state/zip as a single display line, e.g. "Anytown, CA 90001".
 *
 * @param city - City name
 * @param state - Two-letter (or full) state code; uppercased when present
 * @param zip - Postal code
 * @returns The combined "city, STATE zip" line
 */
export const formatCityStateZip = (
  city?: string,
  state?: string,
  zip?: string,
): string => {
  const cityState = [city, state?.toUpperCase()].filter(Boolean).join(', ');
  return [cityState, zip].filter(Boolean).join(' ');
};

export const isIxoPayGateway = (gatewayName?: string): boolean =>
  gatewayName === PAYMENT_GATEWAY_NAME.IXOPAY;

export const isCardknoxGateway = (gatewayName?: string): boolean =>
  gatewayName === PAYMENT_GATEWAY_NAME.CARDKNOX;

/**
 * Build the payment-method list URL for a user, optionally scoped to a school.
 *
 * @param userId - The user whose payment methods to fetch
 * @param schoolID - When provided, scopes the request via a `schoolID` query param
 */
export const getPaymentMethodUrl = ({
  schoolID,
  userId,
}: {
  schoolID?: string;
  userId: string;
}): string =>
  `${PAYMENT_METHOD_API_URL.replace('{userId}', userId)}${
    schoolID ? `?schoolID=${encodeURIComponent(schoolID)}` : ''
  }`;

/**
 * Check if the selected payment method is Google Pay
 *
 * @param selectedPaymentMethod - The selected payment method identifier
 * @returns Whether the payment method is Google Pay
 */
export function isGooglePay(selectedPaymentMethod: string): boolean {
  return selectedPaymentMethod === PAYMENT_METHOD.GOOGLE_PAY;
}

/**
 * Check if the selected payment method is Apple Pay
 *
 * @param selectedPaymentMethod - The selected payment method identifier
 * @returns Whether the payment method is Apple Pay
 */
export function isApplePay(selectedPaymentMethod: string): boolean {
  return selectedPaymentMethod === PAYMENT_METHOD.APPLE_PAY;
}

/**
 * Check if the payment method is Google Pay (for invoice payment methods)
 *
 * @param paymentMethod - The invoice payment method from ExistingPaymentMethodForInvoice enum
 * @returns Whether the payment method is Google Pay
 */
export function isGooglePayInvoiceMethod(paymentMethod: string): boolean {
  return paymentMethod === ExistingPaymentMethodForInvoice.PayWithGooglePay;
}

/**
 * Check if the payment method is Apple Pay (for invoice payment methods)
 *
 * @param paymentMethod - The invoice payment method from ExistingPaymentMethodForInvoice enum
 * @returns Whether the payment method is Apple Pay
 */
export function isApplePayInvoiceMethod(paymentMethod: string): boolean {
  return paymentMethod === ExistingPaymentMethodForInvoice.PayWithApplePay;
}

/**
 * Get the list of allowed payment methods for a school.
 *
 * Falls back to all method keys when the config is absent (backward compat).
 *
 * @param paymentData - Payment summary data containing school config
 * @returns Array of allowed payment method config keys
 */
export function getAllowedPaymentMethods(
  paymentData?: PaymentSummary,
): string[] {
  return (
    paymentData?.SchoolPaymentConfig?.allowedPaymentMethods ??
    ALL_PAYMENT_METHOD_KEYS
  );
}

/**
 * Check if a given payment method config key is in the allowed list.
 *
 * @param method - Config key (e.g. 'card', 'bank', 'googlepay')
 * @param allowedMethods - List returned by getAllowedPaymentMethods
 * @returns Whether the method is allowed
 */
export function isPaymentMethodAllowed(
  method: string,
  allowedMethods: string[],
): boolean {
  const normalizedMethod = PAYMENT_METHOD_TYPE_MAP[method] ?? method;
  return allowedMethods.includes(normalizedMethod);
}

export function isBankAllowed(allowedMethods: string[]): boolean {
  return isPaymentMethodAllowed(PAYMENT_METHOD_KEY.ACH, allowedMethods);
}

export function isCardAllowed(allowedMethods: string[]): boolean {
  return isPaymentMethodAllowed(PAYMENT_METHOD_KEY.CREDIT_CARD, allowedMethods);
}

export function canAddNewPaymentMethod(allowedMethods: string[]): boolean {
  return isCardAllowed(allowedMethods) || isBankAllowed(allowedMethods);
}

/**
 * Get the default payment method value string for a user's saved methods.
 *
 * Filters saved methods to only those allowed by school config, then returns
 * the default (or first) method encoded as "id--maskedNumber--method".
 *
 * @param paymentData - Payment summary data containing school config
 * @param paymentMethods - List of saved payment methods for the user
 * @returns Encoded default payment method string, or empty string if none
 */
export function getDefaultPaymentMethod(
  paymentData: PaymentSummary | undefined,
  paymentMethods: PaymentMethod[],
): string {
  const allowed = getAllowedPaymentMethods(paymentData);
  const allowedSaved = paymentMethods.filter((paymentMethod) =>
    isPaymentMethodAllowed(paymentMethod.method, allowed),
  );
  const defaultValue =
    allowedSaved.find(({ isDefault }) => isDefault) ?? allowedSaved[0];
  if (!defaultValue) {
    return '';
  }
  return `${defaultValue.paymentMethodMappingID}--${defaultValue.maskedNumber}--${defaultValue.method}`;
}

/**
 * Format a saved payment method's display label.
 *
 * Cards render as "VISA (...1234)", bank accounts as "BANK (...5678)".
 *
 * @param paymentMethod - Saved payment method with cardType, maskedNumber, method
 * @returns Display label string
 */
export function formatPaymentMethodLabel(paymentMethod: {
  cardType: string;
  maskedNumber: string;
  method: string;
}): string {
  const last4 = paymentMethod?.maskedNumber?.slice(-4);
  return paymentMethod?.method === 'card'
    ? `${paymentMethod?.cardType?.toUpperCase()} (...${last4})`
    : `BANK (...${last4})`;
}

/**
 * Build dropdown options for a user's saved payment methods, filtered by school config.
 *
 * @param paymentData - Payment summary containing school config
 * @param paymentMethods - User's saved payment methods
 * @param valueFormat - 'encoded' (default) uses "id--maskedNumber--method"; 'id' uses only the mapping ID
 * @returns Allowed methods list, canAddNewMethod flag, and formatted dropdown options
 */
export function buildExistingPaymentMethodOptions({
  paymentData,
  paymentMethods,
  valueFormat = 'encoded',
}: {
  paymentData: PaymentSummary | undefined;
  paymentMethods: PaymentMethod[];
  valueFormat?: ValueFormat;
}): BuildExistingPaymentMethodOptionsResult {
  const allowedMethods = getAllowedPaymentMethods(paymentData);
  const canAddNewMethod = canAddNewPaymentMethod(allowedMethods);
  const existingMethodOptions =
    paymentMethods.length > 0
      ? paymentMethods
          .filter((paymentMethod) =>
            isPaymentMethodAllowed(paymentMethod.method, allowedMethods),
          )
          .map((paymentMethod) => ({
            label: formatPaymentMethodLabel(paymentMethod),
            value:
              valueFormat === 'id'
                ? paymentMethod.paymentMethodMappingID
                : `${paymentMethod.paymentMethodMappingID}--${paymentMethod.maskedNumber}--${paymentMethod.method}`,
          }))
      : [];
  return { allowedMethods, canAddNewMethod, existingMethodOptions };
}

export interface IxoPaySelection {
  isApplePaySelected: boolean;
  isCardSelected: boolean;
  isGooglePaySelected: boolean;
  isHostedType: boolean;
}

export function getIxoPaySelection({
  isIxoPayEnabled,
  selectedPaymentMethod,
}: {
  isIxoPayEnabled: boolean;
  selectedPaymentMethod: string;
}): IxoPaySelection {
  const isCardSelected =
    isIxoPayEnabled &&
    selectedPaymentMethod === ExistingPaymentMethods.PayWithCard;
  const isApplePaySelected =
    isIxoPayEnabled &&
    selectedPaymentMethod === ExistingPaymentMethods.PayWithApplePay;
  const isGooglePaySelected =
    isIxoPayEnabled &&
    selectedPaymentMethod === ExistingPaymentMethods.PayWithGooglePay;
  const isHostedType =
    isIxoPayEnabled &&
    selectedPaymentMethod === ExistingPaymentMethods.PayWithBank;
  return {
    isApplePaySelected,
    isCardSelected,
    isGooglePaySelected,
    isHostedType,
  };
}

export function getPaymentTypeString(value: string): string {
  const map: Record<string, string> = {
    [ExistingPaymentMethods.PayWithBank]: 'Check',
    [ExistingPaymentMethods.PayWithCard]: 'CC',
    [ExistingPaymentMethods.PayWithGooglePay]: PAYMENT_METHOD_TYPE.GOOGLE_PAY,
    [ExistingPaymentMethods.PayWithApplePay]: PAYMENT_METHOD_TYPE.APPLE_PAY,
    [ExistingPaymentMethods.PayWithCheck]: 'cheque',
    [ExistingPaymentMethods.PayWithOther]: 'other',
  };
  return map[value] ?? 'cash';
}

export async function chargePaymentToken({
  apiUrl,
  request,
}: {
  apiUrl: string;
  request: Record<string, unknown>;
}): Promise<ChargeResponseData | undefined> {
  const res = await chargeInvoice(apiUrl, request);
  return res?.data?.data;
}

export async function initiateHostedPayment(
  params: Parameters<typeof chargePaymentToken>[0],
): Promise<string | undefined> {
  const data = await chargePaymentToken(params);
  return data?.redirectUrl;
}

// POLL covers both IxoPay returnType:FINISHED and Cardknox (no returnType) — both require polling.
// REDIRECT covers IxoPay returnType:REDIRECT (e.g. a 3DS challenge) — the user must be sent to redirectUrl.
export enum ChargeOutcome {
  ERROR = 'error',
  POLL = 'poll',
  REDIRECT = 'redirect',
}

export function resolveChargeOutcome(
  responseData?: ChargeResponseData,
): ChargeOutcome {
  if (responseData?.returnType === DETERMINE_PAYMENT_METHOD_RETURN_TYPE.ERROR) {
    return ChargeOutcome.ERROR;
  }
  if (
    responseData?.returnType ===
      DETERMINE_PAYMENT_METHOD_RETURN_TYPE.REDIRECT &&
    responseData?.redirectUrl
  ) {
    return ChargeOutcome.REDIRECT;
  }
  return ChargeOutcome.POLL;
}

export function redirectToHostedPayment(redirectUrl?: string): void {
  if (redirectUrl) {
    window.location.assign(redirectUrl);
  }
}

/**
 * Route a charge response to the right outcome — error, hosted-payment
 * redirect, or transaction-status polling — so callers don't each re-implement
 * the same ChargeOutcome switch.
 *
 * @param responseData - The charge API response
 * @param handlers - onError for ChargeOutcome.ERROR, onPoll (given the transactionID) for everything else pollable
 */
export function handleChargeOutcome(
  responseData: ChargeResponseData | undefined,
  handlers: {
    onError: () => void;
    onPoll: (transactionID: string) => void;
  },
): void {
  switch (resolveChargeOutcome(responseData)) {
    case ChargeOutcome.ERROR:
      handlers.onError();
      break;
    case ChargeOutcome.REDIRECT:
      redirectToHostedPayment(responseData?.redirectUrl);
      break;
    default:
      handlers.onPoll(responseData?.transactionID ?? '');
  }
}

/**
 * Converts status values to display-friendly labels
 *
 * Handles PAST_DUE and OVERDUE cases to show "PAST DUE".
 *
 * @param status - The payment status to convert
 * @returns The display-friendly status label
 */
export function getStatusLabel(status: string): string {
  switch (status) {
    case PaymentStatus.PAST_DUE:
    case AccountSummaryStatus.OVERDUE:
      return PaymentScheduleStatus.PAST_DUE;
    default:
      return status;
  }
}

/**
 * Normalizes a status label for display, e.g. 'CANCELLED' -> "CANCELED",
 * 'PAST_DUE' -> "PAST DUE". The underlying status value used for API
 * filters/matching is untouched.
 *
 * @param label - The status label to display
 * @returns The display-friendly label
 */
export function getDisplayLabel(label: string): string {
  switch (label) {
    case PaymentScheduleStatus.CANCELLED:
      return 'CANCELED';
    case PaymentStatus.PAST_DUE:
      return PaymentScheduleStatus.PAST_DUE;
    default:
      return label;
  }
}
